import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Button, Result, Spin, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../redux/slices/authSlice.ts';
import { selectAuthInitialized } from '../redux/selectors/commonSelectors.ts';
import { AUTH_PORTAL } from './portal.ts';
import type { UserInfo } from '../types/common/common.ts';
import { entraTenantId, loginRequest, tokenRequest } from './msalConfig.ts';
import apiClient from '../api/common/apiClient.ts';
import { setAccessToken } from '../services/auth/token.ts';
import { AUTH_MODE } from '../services/auth/config.ts';
import { AppRoles } from '@qcdoc/mock-runtime/contracts/enums.ts';
import { mockEntraSession } from '../services/auth/mockEntra.ts';

const { Title, Text } = Typography;

/**
 * MsalShell — production auth shell that wraps routes with Entra ID SSO.
 *
 * 1. Triggers an MSAL redirect-based login if the user is not authenticated.
 * 2. Once authenticated, maps Entra claims into Redux auth state.
 * 3. Renders children via `<Outlet />`.
 */
export default function MsalShell() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  // The store is the single source of truth for "claims have been mapped", so there is
  // no local mirror of it to keep in sync from inside the effect.
  const isInitialized = useSelector(selectAuthInitialized);
  const currentUser = useSelector((state: { auth: { user: UserInfo | null } }) => state.auth.user);
  const [authError, setAuthError] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  const dispatch = useDispatch();
  const loginStarted = useRef(false);
  const initializedAccount = useRef<string | null>(null);

  const signIn = useCallback(() => {
    loginStarted.current = true;
    setPopupDismissed(false);
    instance.loginPopup(loginRequest)
      .then((result) => { if (result.account) instance.setActiveAccount(result.account); })
      .catch(() => setPopupDismissed(true));
  }, [instance]);

  useEffect(() => {
    // Re-arm after sign-out so the Microsoft account picker opens again.
    if (isAuthenticated) { loginStarted.current = false; return; }
    if (loginStarted.current || inProgress !== InteractionStatus.None) return;
    signIn();
  }, [inProgress, isAuthenticated, signIn]);

  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const accountId = account?.localAccountId ?? account?.homeAccountId;

  useEffect(() => {
    if (!isAuthenticated || !account || !accountId) {
      initializedAccount.current = null;
      return;
    }
    // Rehydrated mock sessions start with isInitialized=false, so a page refresh resets them
    // to the mock no-access identity. Explicit profile switches set isInitialized=true and must
    // survive the subtree remount triggered by the app's session boundary.
    if (AUTH_MODE === 'mock-msal' && currentUser && isInitialized) {
      initializedAccount.current = accountId;
      if (instance.getActiveAccount()?.homeAccountId !== account.homeAccountId) instance.setActiveAccount(account);
      return;
    }
    if (initializedAccount.current === accountId) return;
    initializedAccount.current = accountId;
    setAuthError(false);
    instance.setActiveAccount(account);

    let cancelled = false;
    void (async () => {
      try {
        const token = AUTH_MODE === 'mock-msal' ? null : await instance.acquireTokenSilent({
          scopes: tokenRequest.scopes,
          account,
          forceRefresh: true,
        });
        if (cancelled) return;
        const claims = (token?.idTokenClaims ?? account.idTokenClaims) as Record<string, unknown> | undefined;
        if (claims?.['tid'] !== entraTenantId) throw new Error('Unexpected Entra tenant.');
        let user: UserInfo = {
          sub: account.localAccountId ?? account.homeAccountId ?? '',
          email: account.username ?? '',
          name: account.name ?? '',
          preferredUsername: account.username ?? '',
          groups: extractGroups(claims),
          psl: typeof claims?.['psl'] === 'string' ? claims['psl'] : undefined,
          portal: AUTH_PORTAL,
        };
        if (AUTH_MODE === 'mock-msal') {
          const mockSession = mockEntraSession('No Access');
          dispatch(authActions.setSession(mockSession));
          setAccessToken(mockSession.token);
          user = mockSession.user;
        } else {
          if (!token) throw new Error('Access token acquisition returned no token.');
          setAccessToken(token.accessToken);
          const response = await apiClient.get<NonNullable<UserInfo['packaging']>>('/api/Auth/packaging-permissions');
          user.packaging = response.data;
        }
        if (!cancelled) {
          dispatch(authActions.setUser(user));
          dispatch(authActions.setInitialized(true));
        }
      } catch {
        if (!cancelled) {
          initializedAccount.current = null;
          setAccessToken(null);
          setAuthError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, accountId, currentUser, isInitialized, instance, dispatch]);

  if (authError) return <Result status="403" title="Sign-in could not be verified" subTitle="Check your corporate account and try signing in again." />;

  const identityMismatch = AUTH_MODE !== 'mock-msal' && currentUser?.sub !== accountId;
  if (!isAuthenticated || !isInitialized || identityMismatch) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 16,
        }}
      >
        <Title level={3}>QCDOC</Title>
        {!isAuthenticated && popupDismissed ? <>
          <Text type="secondary">The Microsoft sign-in window was closed or blocked.</Text>
          {/* Browsers only allow reopening a popup from a user gesture. */}
          <Button type="link" onClick={signIn}>Open Microsoft sign-in</Button>
        </> : <>
          <Spin size="large" />
          <Text type="secondary">{isAuthenticated ? 'Authenticating with Entra ID...' : 'Waiting for Microsoft sign-in...'}</Text>
        </>}
      </div>
    );
  }

  return <Outlet />;
}

// ── Helpers ──

/**
 * Extracts group names from Entra token claims.
 * Entra can send groups as `groups` (object IDs) or `roles` (app role values).
 * Prefer app-role values if available.
 */
export function extractGroups(claims: Record<string, unknown> | undefined): string[] {
  if (!claims) return [];

  const knownRoles: Set<string> = new Set(Object.values(AppRoles));
  const roles = Array.isArray(claims['roles']) ? claims['roles'] : [];
  let groupRoles: unknown[] = [];
  if (import.meta.env.VITE_ENTRA_GROUP_ROLE_MAP && Array.isArray(claims['groups'])) {
    const mapping: unknown = JSON.parse(import.meta.env.VITE_ENTRA_GROUP_ROLE_MAP);
    if (mapping && typeof mapping === 'object' && !Array.isArray(mapping)) {
      groupRoles = claims['groups'].map((group: unknown) =>
        typeof group === 'string' ? (mapping as Record<string, unknown>)[group] : undefined);
    }
  }
  return [...new Set([...roles, ...groupRoles].filter((role): role is string =>
    typeof role === 'string' && knownRoles.has(role)))];
}
